import { Box, Container, styled } from "@material-ui/core";
import { graphql } from "gatsby";
import get from "lodash/get";
import React from "react";
import ContentModules, { ContainerMaxWidth } from "../../content-modules";
import { isPageDisabledByFeatureFlags } from "../../helpers/feature-flags";
import useCountry from "../../hooks/use-country";
import useSubcountry from "../../hooks/use-subcountry";
import useUnlocalizedLocationPath from "../../hooks/use-unlocalized-location-path";
import theme from "../../theme/material-ui-theme";
import NoSsrPlaceholder from "../no-ssr-placeholder";
import NotFound from "../not-found";
import Seo from "../seo";
import PageDocument from "./PageDocument";
import Header, { PageHeaderProps } from "./PageHeader";
import cn from "classnames";

type PageProps = Partial<Queries.WebPageFragmentFragment> & {
  children?: React.ReactNode;
  HeaderProps?: PageHeaderProps;
  container?: ContainerMaxWidth;
  className?: string;
};

const PageContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    "&.overlaps": {
      marginTop: "calc(-1 * var(--header-height))"
    }
  }
}));

/**
 * DatoCMS WebPage
 */
export default function Page({
  seo,
  seoMetaTags,
  title,
  descriptionNode,
  documentNode,
  HeaderProps,
  modules,
  children,
  container,
  containerDisableGutters,
  background,
  className,
  headerVariant
}: PageProps) {
  const description =
    descriptionNode && descriptionNode?.childMarkdownRemark.html;
  const document = documentNode && documentNode?.childMarkdownRemark.html;

  const containerStyles: React.CSSProperties = {};
  const containerClassnames = ["page-wrapper", className];
  if (headerVariant === "overlaps") {
    containerClassnames.push("overlaps");
  }

  // Can be hex code or palette key: "primary.main"
  if (background) {
    containerStyles.backgroundColor = background.startsWith("#")
      ? background
      : get(theme.palette, background);
  }

  // Render 404 if current page is disabled by feature flags
  const country = useCountry();
  const { subcountry, isSubcountryMounted } = useSubcountry();
  const unlocalizedPath = useUnlocalizedLocationPath();
  if (isPageDisabledByFeatureFlags(country, unlocalizedPath, { subcountry })) {
    return isSubcountryMounted ? <NotFound /> : <NoSsrPlaceholder />;
  }

  return (
    <PageContainer className={cn(containerClassnames)} style={containerStyles}>
      {seoMetaTags && <Seo seo={seo} seoMetaTags={seoMetaTags} />}

      {/* Custom pages can specify a children component */}
      {children}

      {/* Page title (Only used by static documents) */}
      {title && (
        <Header
          title={title}
          description={description}
          {...(HeaderProps || {})}
        />
      )}

      {/* Page content modules */}
      {!document && (
        <>
          {modules && modules.length > 0 && (
            <ContentModules
              contentModules={modules}
              pageContainer={container}
              pageContainerDisableGutters={containerDisableGutters}
            />
          )}
        </>
      )}

      {/* Documents are for text only pages like TOS/Privacy policy, etc */}
      {/* It should be refactored into a content module */}
      {document && (
        <Container maxWidth={container === "fluid" ? false : container}>
          <PageDocument html={document} />
        </Container>
      )}
    </PageContainer>
  );
}

export const WebPageFragment = graphql`
  fragment WebPageFragment on DatoCmsWebPage {
    seo {
      title
    }
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    path
    title
    descriptionNode {
      childMarkdownRemark {
        html
      }
    }
    documentNode {
      childMarkdownRemark {
        html
      }
    }
    container
    containerDisableGutters
    background
    headerVariant
    ...WebPageContentModulesFragment
  }
`;
